import {inject, Injectable}                                                                                                                          from '@angular/core';
import {CrudRecordType, CrudRecordTypes, GlobalDataTypesDict, GlobalRecordType, HkitRecordLogType, HkitRecordLogTypes, RizeSessionType, TwColorType} from '@djsharkk/logr-types';
import {ChartDataset, ChartOptions, Point, ScriptableContext}                                                                                        from 'chart.js';
import {AnnotationOptions}                                                                                                                           from 'chartjs-plugin-annotation';
import cloneDeep                                                                                                                                     from 'lodash/cloneDeep';
import {DateTime}                                                                                                                                    from 'luxon';
import * as patternomaly                                                                                                                             from 'patternomaly';
import {DataFilterService}                                                                                                                           from '../../logr-services/data/data-filter.service';
import {UiThemeService}                                                                                                                              from '../../logr-ui/utils/ui-theme.service';
import {ChartConfsService}                                                                                                                           from './chart-confs.service';
import {ChartThemeService}                                                                                                                           from './chart-theme.service';


export type ChartStylesDict<T extends string, D> = {
	[key in T]: D
};

export type CrudDefaultObj = {
	[key in CrudRecordType]: Object
}

@Injectable({
				providedIn: 'root'
			})
export class ChartPropsService {
	private svcChartConfs = inject(ChartConfsService);
	private svcChartTheme = inject(ChartThemeService);
	private svcDataFilter = inject(DataFilterService);
	private svcUiTheme = inject(UiThemeService)

	chartLabelsDaily = this.buildDailyLabels();

	chartOptionsBase: ChartOptions = this.svcChartConfs.defaultChartOptions();

	crudDefault = (): CrudDefaultObj => ({
		vape: {},
		meds: {},
		mood: {},
		mind: {},
		body: {},
		caff: {},
		food: {}
	})

	private chartOptionsHour: ChartOptions<'line'> = this.svcChartConfs.timeChartOptions(
		'hour',
		{
			hour: 'HH'
		}
	) as ChartOptions<'line'>;


	barStyleBase = (color: TwColorType): ChartOptions<any> => ({
		backgroundColor: this.svcUiTheme.getTailwindColor(color, 800, 0.5),
		borderColor    : 'transparent',
		borderWidth    : 0,
		barThickness   : (ctx: ScriptableContext<'line'>) => Math.max(1, (ctx.chart.scales['x'].maxWidth / ctx.dataset.data.length)) - 2
	})

	pointStylesCommonBase = (type: GlobalRecordType): Partial<ChartDataset<'line'>> => ({
		label: GlobalDataTypesDict[type].name
	})

	pointStylesSummaryBase = (type: GlobalRecordType): Partial<ChartDataset<'line'>> => ({
		...this.pointStylesCommonBase(type),
		pointStyle      : 'line',
		pointRadius     : 7,
		pointHitRadius  : 8,
		pointRotation   : 90,
		borderColor     : GlobalDataTypesDict[type].colorHex || this.svcUiTheme.getTailwindColor(GlobalDataTypesDict[type].colorTw, 600),
		pointBorderWidth: 1
	})

	pointStylesDetailBase = (type: GlobalRecordType): Partial<ChartDataset<'line'>> => ({
		...this.pointStylesCommonBase(type),
		pointStyle      : 'triangle',
		pointHitRadius  : 8,
		backgroundColor : GlobalDataTypesDict[type].colorHex || this.svcUiTheme.getTailwindColor(GlobalDataTypesDict[type].colorTw, 600),
		pointBorderWidth: 0,
		pointRadius     : 2
	})


	vapeBarAllStyle: Partial<ChartDataset<'bar'>> = {
		...this.barStyleBase('neutral')
	}

	avgStyle: ChartDataset<'line', Point[]> = {
		data          : [],
		label         : '3 Day Moving Average',
		type          : 'line',
		borderCapStyle: 'butt',
		pointRadius   : 0,
		borderColor   : this.svcUiTheme.getTailwindColor('orange', 600),
		borderWidth   : 1
	}

	crudPointStylesDetail: ChartStylesDict<CrudRecordType, Partial<ChartDataset<'scatter'>>> = CrudRecordTypes.reduce(
		(entries, type) => {
			entries[type] = this.pointStylesDetailBase(type);
			return entries;
		},
		this.crudDefault()
	);

	crudPointStylesSummary: ChartStylesDict<CrudRecordType, Partial<ChartDataset<'scatter'>>> = CrudRecordTypes.reduce(
		(entries, type) => {
			entries[type] = this.pointStylesSummaryBase(type);
			return entries;
		},
		this.crudDefault()
	);

	hkitPointStylesDetail: ChartStylesDict<HkitRecordLogType, Partial<ChartDataset<'scatter'>>> = HkitRecordLogTypes.reduce(
		(entries, type) => {
			entries[type] = this.pointStylesDetailBase(type);
			return entries;
		},
		{
			blood_oxygen_saturation     : {},
			dietary_water               : {},
			environmental_audio_exposure: {},
			handwashing                 : {},
			headphone_audio_exposure    : {},
			respiratory_rate            : {},
			toothbrushing               : {},
			weight_body_mass            : {}
		}
	);

	hkitPointStylesSummary: ChartStylesDict<HkitRecordLogType, Partial<ChartDataset<'scatter'>>> = HkitRecordLogTypes.reduce(
		(entries, type) => {
			entries[type] = this.pointStylesSummaryBase(type);
			return entries;
		},
		{
			blood_oxygen_saturation     : {},
			dietary_water               : {},
			environmental_audio_exposure: {},
			handwashing                 : {},
			headphone_audio_exposure    : {},
			respiratory_rate            : {},
			toothbrushing               : {},
			weight_body_mass            : {}
		}
	);


	rizeColors: {
		[key in RizeSessionType]: TwColorType
	} = {
		focus  : 'orange',
		break  : 'indigo',
		meeting: 'pink'
	}

	rizePatternsDark: {
		[key in RizeSessionType]: CanvasPattern
	} = {
		focus  : patternomaly.draw(
			'triangle',
			this.svcUiTheme.getTailwindColor(this.rizeColors['focus'], 950, 0),
			this.svcUiTheme.getTailwindColor(this.rizeColors['focus'], 950),
			6
		),
		break  : patternomaly.draw(
			'disc',
			this.svcUiTheme.getTailwindColor(this.rizeColors['break'], 950, 0),
			this.svcUiTheme.getTailwindColor(this.rizeColors['break'], 950),
			6
		),
		meeting: patternomaly.draw(
			'diagonal',
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.sleep.colorTw, 700, 0.25),
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.sleep.colorTw, 700),
			6
		)

	}

	rizePatterns: {
		[key in RizeSessionType]: CanvasPattern
	} = {
		focus  : patternomaly.draw(
			'disc',
			this.svcUiTheme.getTailwindColor(this.rizeColors['focus'], 950, 0),
			this.svcUiTheme.getTailwindColor(this.rizeColors['focus'], 950),
			6
		),
		break  : patternomaly.draw(
			'disc',
			this.svcUiTheme.getTailwindColor(this.rizeColors['break'], 950, 0),
			this.svcUiTheme.getTailwindColor(this.rizeColors['break'], 950),
			6
		),
		meeting: patternomaly.draw(
			'diagonal',
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.sleep.colorTw, 400, 0.25),
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.sleep.colorTw, 400),
			6
		)

	}

	stripeAnnotationStylesWeekends: {
		[key in 'sleep' | 'workout' | 'hidden']: CanvasPattern
	} = {
		sleep  : patternomaly.draw(
			'diagonal',
			this.svcUiTheme.getTailwindColor('blue', 950, 0),
			this.svcUiTheme.getTailwindColor('blue', 950, 0.75),
			8
		),
		workout: patternomaly.draw(
			'diagonal',
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.workout.colorTw, 900, 0),
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.workout.colorTw, 900),
			8
		),
		hidden : patternomaly.draw(
			'disc',
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.workout.colorTw, 900, 0),
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.workout.colorTw, 900),
			8
		)

	}

	stripeAnnotationStylesWeekdays: {
		[key in 'sleep' | 'workout' | 'hidden']: CanvasPattern
	} = {
		sleep  : patternomaly.draw(
			'diagonal',
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.sleep.colorTw, 900, 0),
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.sleep.colorTw, 900),
			8
		),
		workout: patternomaly.draw(
			'diagonal',
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.workout.colorTw, 950, 0.5),
			this.svcUiTheme.getTailwindColor(GlobalDataTypesDict.workout.colorTw, 800),
			8
		),
		hidden : patternomaly.draw(
			'disc',
			this.svcUiTheme.getTailwindColor('neutral', 800, 0),
			this.svcUiTheme.getTailwindColor('neutral', 800),
			6
		)

	}


	constructor() {
		this.chartOptionsHour.scales = {
			...this.chartOptionsHour.scales,
			x: {
				...this.chartOptionsHour.scales!['x'],
				ticks: {
					...this.chartOptionsHour.scales?.['x']?.ticks,
					callback: (tickValue, i, ticks) => {
						return i === 0 /*|| i === ticks.length - 1*/ ? '' : DateTime.fromMillis(tickValue as number).toFormat('HH');
						// return DateTime.fromMillis(tickValue as number).toFormat('HH');
					}
				}
			}
		};

	}

	get optsDefault(): ChartOptions {
		return cloneDeep(this.svcChartConfs.defaultChartOptions());
	}

	get optsDoughnut(): ChartOptions<'doughnut'> {
		return cloneDeep(this.svcChartConfs.doughnutChartOptions());
	}

	get optsHour(): ChartOptions<'line' | 'scatter'> {
		return cloneDeep(this.chartOptionsHour);
	}

	get optsDayBarStacked(): ChartOptions<'bar'> {
		return {...this.chartOptionsDayBarStacked()};
	}

	get optsDayBar(): ChartOptions<'bar'> {
		return {...this.chartOptionsDayBar()};
	}

	chartOptionsDayBar = (): ChartOptions<'bar'> => {
		const opts = this.svcChartConfs.timeChartOptions('day', {
			day: 'dd'
		}) as ChartOptions<'bar'>;

		opts.scales = {
			x: {
				...opts.scales!['x'],
				offset: true
			},
			y: {
				...opts.scales!['y']
			}
		};

		return opts;
	};

	chartOptionsDayBarStacked = (): ChartOptions<'bar'> => {
		const opts = this.svcChartConfs.timeChartOptions('day', {
			day: 'dd'
		}) as ChartOptions<'bar'>;

		opts.scales = {
			x: {
				...opts.scales!['x'],
				stacked: true,
				offset : true
			},
			y: {
				...opts.scales!['y'],
				stacked: true
			}
		};

		return opts;
	};

	buildHourlyLabels(timeframe: Date = new Date()): Date[] {
		return Array(25)
			.fill(0)
			.map((_, i) => {
				const nd = new Date(timeframe);
				nd.setHours(i, 0, 0, 0);
				return nd;
			});
	}

	buildDailyLabels(timeframe: Date[] = this.svcDataFilter.dateRange.getValue()): Date[] {
		return timeframe;
	}

	buildAnnotationKey = (d: Date) => DateTime.fromJSDate(d).toFormat('yyyyLLdd');


	addNowLine = (options: ChartOptions) => {
		this.buildAnnotation({
								 type       : 'line',
								 xMin       : new Date().getTime(),
								 xMax       : new Date().getTime(),
								 borderWidth: 1,
								 borderDash : [
									 4,
									 4
								 ],
								 borderColor: this.svcUiTheme.getTailwindColor('neutral', 800)
							 }, 'now', options)
	}

	buildAnnotation(annotation: AnnotationOptions, keyName: string, opts: ChartOptions): void {
		const annotationPlugin = opts?.plugins?.annotation;
		if (!annotationPlugin) {
			return;
		}

		const existingAnnotations = (annotationPlugin.annotations as Record<string, AnnotationOptions>) || {};
		annotationPlugin.annotations = {
			...existingAnnotations,
			[keyName]: annotation
		};
	}
}
